import React, { useState, useMemo, useRef, useEffect } from 'react';
import Select from 'react-select';
import { useSelectStyles } from '../utils/selectStyles';

// Hook personnalisé pour le debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Fonction pour obtenir la valeur brute d'une cellule, qu'elle utilise render ou non
const getRawCellValue = (item, column) => {
  // if column has a rawValue method, use it
  if (column.rawValue && typeof column.rawValue === 'function')
    return column.rawValue(item);

  return item[column.key];
};

// Fonction utilitaire pour convertir les données en CSV
const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return '';

  // En-têtes du CSV
  const headers = columns.map(col => col.label).join(',');

  // Lignes de données
  const rows = data.map(item => {
    return columns.map(column => {
      // Récupérer la valeur brute pour l'export
      let value = getRawCellValue(item, column);

      // Convertir en chaîne et échapper les virgules et guillemets
      if (value === null || value === undefined) value = '';
      value = String(value);

      // Si la valeur contient une virgule, des guillemets ou un saut de ligne, l'entourer de guillemets
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        // Échapper les guillemets en les doublant
        value = value.replace(/"/g, '""');
        // Entourer de guillemets
        value = `"${value}"`;
      }

      return value;
    }).join(',');
  }).join('\n');

  return `${headers}\n${rows}`;
};

// Fonction pour déclencher le téléchargement
const downloadCSV = (csvContent, filename = 'export.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const SortableTable = ({
  data,
  columns,
  emptyMessage,
  countLabel,
  disableFilters = false,
  initialSortBy = null,
  exportable = false,
  exportFilename = 'export.csv',
  exportRenamable = false,
  onRowClick = null,
  fullHeight = false,
  className = null,
  globalSearch = false,
  withSum = false,
  hideCountLabel = false
}) => {
  const [sortConfig, setSortConfig] = useState(
    initialSortBy || {
      key: columns && columns.length > 0 ? columns[0].key : null,
      direction: 'ascending'
    }
  );
  const [filters, setFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [customExportFilename, setCustomExportFilename] = useState('');
  const filterSelectRef = useRef(null);
  const selectInputRef = useRef(null);
  const exportInputRef = useRef(null);

  // Debounce du terme de recherche globale (300ms de délai)
  const debouncedGlobalSearchTerm = useDebounce(globalSearchTerm, 300);

  const selectStyles = useSelectStyles();
  // Pour fermer le filtre lors d'un clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterSelectRef.current && !filterSelectRef.current.contains(event.target)) {
        setActiveFilterColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-focus sur l'input du Select quand le filtre s'ouvre
  useEffect(() => {
    if (activeFilterColumn && selectInputRef.current) {
      // Petit délai pour s'assurer que le Select est complètement rendu
      setTimeout(() => {
        if (selectInputRef.current) {
          selectInputRef.current.focus();
        }
      }, 100);
    }
  }, [activeFilterColumn]);

  // Auto-focus sur l'input d'export quand la modal s'ouvre
  useEffect(() => {
    if (isExportModalOpen && exportInputRef.current) {
      setTimeout(() => {
        if (exportInputRef.current) {
          exportInputRef.current.focus();
          exportInputRef.current.select();
        }
      }, 100);
    }
  }, [isExportModalOpen]);

  // Modifié pour générer les options de filtre à partir des données filtrées
  const dynamicColumnOptions = useMemo(() => {
    const options = {};

    columns.forEach(column => {
      if (column.filterable !== false) {
        // Créer un Set pour les valeurs uniques
        const uniqueValues = new Set();

        // Déterminer quelles données utiliser pour ce filtre
        // Filtrer les données en appliquant tous les filtres SAUF celui de la colonne actuelle
        let dataForThisColumn = [...data];

        Object.keys(filters).forEach(key => {
          // Ne pas appliquer le filtre de la colonne en cours
          if (key !== column.key) {
            const filterValues = filters[key];
            if (filterValues && filterValues.length > 0) {
              const filterColumn = columns.find(col => col.key === key);
              dataForThisColumn = dataForThisColumn.filter(item => {
                const itemValue = getRawCellValue(item, filterColumn);
                return filterValues.includes(String(itemValue));
              });
            }
          }
        });

        // Extraire les valeurs uniques à partir des données filtrées
        dataForThisColumn.forEach(item => {
          const value = getRawCellValue(item, column);
          if (value !== undefined && value !== null) {
            uniqueValues.add(String(value));
          }
        });

        // Convertir les valeurs uniques en options pour react-select
        options[column.key] = Array.from(uniqueValues)
          .sort()
          .map(value => ({ value, label: value }));
      }
    });

    return options;
  }, [data, columns, filters]);

  // Handle global search
  const handleGlobalSearch = (event) => {
    setGlobalSearchTerm(event.target.value);
  };

  // Clear global search
  const clearGlobalSearch = () => {
    setGlobalSearchTerm('');
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Toggle filter dropdown
  const toggleFilter = (columnKey) => {
    setActiveFilterColumn(activeFilterColumn === columnKey ? null : columnKey);
  };

  // Handle filter change
  const handleFilterChange = (selectedOptions, columnKey) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      const newFilters = { ...filters };
      delete newFilters[columnKey];
      setFilters(newFilters);
    } else {
      setFilters({
        ...filters,
        [columnKey]: selectedOptions.map(option => option.value)
      });
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filteredData = [...data];

    // Apply column filters
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        const column = columns.find(col => col.key === key);

        filteredData = filteredData.filter(item => {
          const itemValue = getRawCellValue(item, column);

          return filterValues.includes(String(itemValue));
        });
      }
    });

        // Apply global search (avec debounce)
    if (globalSearch && debouncedGlobalSearchTerm.trim().length > 0) {
      const searchTerm = debouncedGlobalSearchTerm.toLowerCase().trim();

      filteredData = filteredData.filter(item => {
        // Search in all columns
        return columns.some(column => {
          let searchValue;

          // Get the value to search in
          if (column.render) {
            // For rendered columns, use raw value if available, otherwise the key value
            searchValue = getRawCellValue(item, column);
          } else {
            searchValue = item[column.key];
          }

          // Convert to string and search
          if (searchValue !== null && searchValue !== undefined) {
            return String(searchValue).toLowerCase().includes(searchTerm);
          }
          return false;
        });
      });
    }

    // Sort filtered data
    return filteredData.sort((a, b) => {
      // Find column for custom sorting
      const column = columns.find(col => col.key === sortConfig.key);

      // Use custom sort function if provided
      if (column && column.sortFunction)
        return column.sortFunction(a, b, sortConfig.direction);

      // Get values to compare - use raw values
      let aValue = column && column.render ?
        getRawCellValue(a, column) :
        a[sortConfig.key];

      let bValue = column && column.render ?
        getRawCellValue(b, column) :
        b[sortConfig.key];

      // Handle undefined or null values
      if (aValue === undefined || aValue === null) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (bValue === undefined || bValue === null) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }

      // Attempt to convert to numbers for numeric comparison (including floats)
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      // If both values can be converted to numbers, use numeric comparison
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'ascending' ? aNum - bNum : bNum - aNum;
      }

      // Otherwise, convert to strings and compare
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      // Default string comparison
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig, columns, filters, debouncedGlobalSearchTerm]);

  // Build numeric summaries (sum/avg) for columns when requested
  const summaryByColumnKey = useMemo(() => {
    if (!withSum) return {};

    const summaries = {};

    columns.forEach((column) => {
      // Sample up to 5 non-null raw values to detect numeric columns
      const samples = [];
      for (let i = 0; i < filteredAndSortedData.length && samples.length < 5; i++) {
        const v = getRawCellValue(filteredAndSortedData[i], column);
        if (v !== undefined && v !== null && v !== '') samples.push(v);
      }

      if (samples.length === 0) {
        summaries[column.key] = { isNumeric: false };
        return;
      }

      const numericSamples = samples
        .map(v => (typeof v === 'number' ? v : Number(String(v).replace(/\s/g, '').replace(',', '.'))))
        .filter(n => Number.isFinite(n));

      // Heuristic: if at least 80% of samples are numeric (or all if <3 samples), treat as numeric
      const isNumeric = samples.length < 3
        ? numericSamples.length === samples.length
        : (numericSamples.length / samples.length) >= 0.8;

      if (!isNumeric) {
        summaries[column.key] = { isNumeric: false };
        return;
      }

      // Determine integer-only formatting based on samples
      const isIntegerOnly = numericSamples.every(n => Number.isInteger(n));

      // Compute sum/avg over ALL filtered rows
      let sum = 0;
      let count = 0;
      for (let i = 0; i < filteredAndSortedData.length; i++) {
        const v = getRawCellValue(filteredAndSortedData[i], column);
        const n = typeof v === 'number' ? v : Number(String(v).replace(/\s/g, '').replace(',', '.'));
        if (Number.isFinite(n)) {
          sum += n;
          count += 1;
        }
      }

      const avg = count > 0 ? (sum / count) : 0;

      summaries[column.key] = {
        isNumeric: true,
        isIntegerOnly,
        sum,
        avg
      };
    });

    return summaries;
  }, [withSum, columns, filteredAndSortedData]);

  // Generate sort indicators
  const getSortDirectionIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return (
        <svg className="w-3 h-3 ml-1 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.58L18 10z" />
        </svg>
      );
    }

    return sortConfig.direction === 'ascending' ? (
      <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24">
        <path d="M7 14l5-5 5 5H7z" />
      </svg>
    ) : (
      <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24">
        <path d="M7 10l5 5 5-5H7z" />
      </svg>
    );
  };

  // Generate filter indicator
  const getFilterIcon = (columnKey) => {
    if (disableFilters) return null;

    const isFiltered = filters[columnKey] && filters[columnKey].length > 0;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleFilter(columnKey);
        }}
        className={`inline-flex items-center justify-center w-4 h-4 ml-2 ${isFiltered ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
        style={{ minWidth: '16px', minHeight: '16px' }}
      >
        <svg
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
        >
          <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd"></path>
        </svg>
      </button>
    );
  };

  // Fonction pour ouvrir la modal d'export ou exporter directement
  const handleExport = () => {
    if (exportRenamable) {
      setCustomExportFilename(exportFilename);
      setIsExportModalOpen(true);
    } else {
      const csvContent = convertToCSV(filteredAndSortedData, columns);
      downloadCSV(csvContent, exportFilename);
    }
  };

  // Fonction pour confirmer l'export avec le nom personnalisé
  const handleConfirmExport = () => {
    const finalFilename = customExportFilename.trim() || exportFilename;
    const csvContent = convertToCSV(filteredAndSortedData, columns);
    downloadCSV(csvContent, finalFilename);
    setIsExportModalOpen(false);
  };

  // Fonction pour gérer la soumission du formulaire d'export (Enter)
  const handleExportFormSubmit = (e) => {
    e.preventDefault();
    handleConfirmExport();
  };

  // Fonction pour fermer la modal d'export
  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setCustomExportFilename('');
  };

  // Determine the container class based on fullHeight prop
  const containerClass = className || (fullHeight
    ? 'flex flex-col h-full relative'
    : 'flex flex-col h-full max-h-[500px] relative');

  return (
    <div className={containerClass}>
      {/* Global Search Bar */}
      {globalSearch && (
        <div className="flex justify-end mb-4">
          <div className="relative w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Rechercher dans le tableau..."
              value={globalSearchTerm}
              onChange={handleGlobalSearch}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            {globalSearchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={clearGlobalSearch}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Effacer la recherche"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="overflow-auto flex-grow">
        {data.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    <div className="flex items-center relative">
                      <div
                        onClick={() => requestSort(column.key)}
                        className="flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        <span>{column.label}</span>
                        {getSortDirectionIcon(column.key)}
                      </div>

                      {column.filterable !== false && !disableFilters && (
                        <>
                          {getFilterIcon(column.key)}

                          {activeFilterColumn === column.key && (
                            <div
                              ref={filterSelectRef}
                              className={`absolute ${
                                columns.findIndex(col => col.key === column.key) === 0
                                  ? 'left-0'
                                  : 'right-0'
                              } top-6 mt-1 w-64 z-50 bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-2">
                                <Select
                                  ref={selectInputRef}
                                  isMulti
                                  closeMenuOnSelect={false}
                                  options={dynamicColumnOptions[column.key] || []}
                                  onChange={(selected) => handleFilterChange(selected, column.key)}
                                  value={(filters[column.key] || []).map(value => ({ value, label: value }))}
                                  placeholder="Filtrer..."
                                  classNamePrefix="react-select"
                                  className="text-sm text-gray-700 dark:text-gray-300"
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                  autoFocus={true}
                                />

                                <div className="flex justify-between mt-2">
                                  <button
                                    onClick={() => handleFilterChange([], column.key)}
                                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                  >
                                    Réinitialiser
                                  </button>
                                  <button
                                    onClick={() => setActiveFilterColumn(null)}
                                    className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                  >
                                    Appliquer
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              {withSum && (
                <tr>
                  {columns.map((column) => {
                    const s = summaryByColumnKey[column.key];
                    let content = '';
                    if (s && s.isNumeric) {
                      const format = (n) => s.isIntegerOnly ? Math.round(n).toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
                      content = `Σ ${format(s.sum)}  •  Avg ${format(s.avg)}`;
                    }
                    return (
                      <th
                        key={`sum-${column.key}`}
                        className="px-4 py-2 text-left text-[11px] font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap"
                      >
                        {content}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedData.map((item, index) => {
                if (item.rawValue && typeof item.rawValue === 'function')
                  item.rawValue = item.rawValue(item);

                return <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${onRowClick ? 'cursor-pointer' : ''}`} onClick={() => onRowClick && onRowClick(item)}>
                  {columns.map((column) => (
                    <td key={`${index}-${column.key}`} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 text-left">
                      {column.render ? column.render(item) : item[column.key]}
                    </td>
                  ))}
                </tr>
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400 flex-grow flex items-center justify-center">
            <div>
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-left">{emptyMessage || "Aucune donnée disponible"}</p>
            </div>
          </div>
        )}
      </div>

      {/* Barre d'informations fixe en bas */}
      {data.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-2 px-4 text-xs text-gray-500 dark:text-gray-400 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {!hideCountLabel && <p>{countLabel || `Total: ${filteredAndSortedData.length} éléments`}</p>}

            {exportable && filteredAndSortedData.length > 0 && (
              <button
                onClick={handleExport}
                className="ml-3 text-xs text-green-600 hover:text-green-800 dark:text-green-500 dark:hover:text-green-400 flex items-center"
                title="Exporter en CSV"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {globalSearch && debouncedGlobalSearchTerm && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Recherche: "<span className="font-medium text-gray-700 dark:text-gray-300">{debouncedGlobalSearchTerm}</span>"
                {globalSearchTerm !== debouncedGlobalSearchTerm && (
                  <span className="ml-1 text-blue-500 dark:text-blue-400">...</span>
                )}
              </div>
            )}

            {!disableFilters && Object.keys(filters).length > 0 && (
              <button
                onClick={() => setFilters({})}
                className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
              >
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
                Effacer tous les filtres
              </button>
            )}
          </div>
        </div>
      )}

      {data.length > 0 && filteredAndSortedData.length === 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-2 px-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          <p>
            Aucun résultat ne correspond aux {globalSearch && debouncedGlobalSearchTerm ? 'critères de recherche' : 'filtres appliqués'}
          </p>
          <div className="mt-2 flex justify-center gap-4">
            {globalSearch && (globalSearchTerm || debouncedGlobalSearchTerm) && (
              <button
                onClick={clearGlobalSearch}
                className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Effacer la recherche
              </button>
            )}
            {Object.keys(filters).length > 0 && (
              <button
                onClick={() => setFilters({})}
                className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal d'export avec nom personnalisé */}
      {isExportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-96 max-w-sm mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Nommer le fichier d'export
            </h3>

            <form onSubmit={handleExportFormSubmit}>
              <div className="mb-4">
                <label htmlFor="export-filename" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nom du fichier CSV
                </label>
                <input
                  ref={exportInputRef}
                  id="export-filename"
                  type="text"
                  value={customExportFilename}
                  onChange={(e) => setCustomExportFilename(e.target.value)}
                  placeholder={exportFilename}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Laissez vide pour utiliser le nom par défaut
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseExportModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Exporter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SortableTable;